import { useCallback, useMemo, useState } from 'react';
import {
  ChevronDownIcon,
  ChevronRightIcon,
  MagnifyingGlassIcon,
} from '@heroicons/react/24/outline';
import { BuildingLibraryIcon, HandRaisedIcon } from '@heroicons/react/24/solid';
import clsx from 'clsx';
import { useAuth } from '@/context/AuthContext';
import { usePrayerUnreadContext } from '@/context/PrayerUnreadContext';
import { useOrgLockState } from '@/hooks/useOrgLockState';
import PausedChip from '@/components/lock/PausedChip';
import PrayerGroupListItem, { type PrayerSidebarGroup } from './PrayerGroupListItem';

const COLLAPSE_STORAGE_KEY = 'flock_prayer_sidebar_collapsed';

function loadCollapsed(): Set<string> {
  try {
    const raw = localStorage.getItem(COLLAPSE_STORAGE_KEY);
    return raw ? new Set(JSON.parse(raw)) : new Set();
  } catch {
    return new Set();
  }
}

interface OrgNode {
  orgId: string;
  orgName: string;
  groups: PrayerSidebarGroup[];
  unread: number;
}

export type PrayerView = 'all' | 'lifted' | { group: PrayerSidebarGroup };

interface Props {
  /** 'all' | 'lifted' | a groupId. */
  selectedKey: string | null;
  onSelectAll: () => void;
  onSelectLifted: () => void;
  onSelectGroup: (group: PrayerSidebarGroup) => void;
}

/**
 * Cross-org prayer sidebar: pinned combined views ("All Prayers", "Lifted")
 * on top, then Organization (collapsible) → community groups with unread
 * prayer-activity badges. Built from memberships, so every group appears
 * even before it has any prayers.
 */
export default function PrayerSidebar({
  selectedKey,
  onSelectAll,
  onSelectLifted,
  onSelectGroup,
}: Props) {
  const { organizations } = useAuth();
  const { byGroup, totalUnread } = usePrayerUnreadContext();
  const { isOrgLocked } = useOrgLockState();
  const [search, setSearch] = useState('');
  const [collapsed, setCollapsed] = useState<Set<string>>(loadCollapsed);

  const toggleCollapsed = useCallback((key: string) => {
    setCollapsed((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      try {
        localStorage.setItem(COLLAPSE_STORAGE_KEY, JSON.stringify(Array.from(next)));
      } catch {
        // Ignore storage errors
      }
      return next;
    });
  }, []);

  const tree = useMemo((): OrgNode[] => {
    const q = search.trim().toLowerCase();
    const orgs: OrgNode[] = [];
    for (const membership of organizations) {
      const orgName = membership.organization.name;
      const groups: PrayerSidebarGroup[] = (membership.groups ?? [])
        .filter(
          (g) =>
            !q || g.name.toLowerCase().includes(q) || orgName.toLowerCase().includes(q)
        )
        .map((g) => ({
          id: g.id,
          name: g.name,
          orgId: membership.organization_id,
          orgName,
          imageUrl: g.group_image_url,
        }))
        .sort((a, b) => a.name.localeCompare(b.name));
      if (groups.length === 0) continue;
      orgs.push({
        orgId: membership.organization_id,
        orgName,
        groups,
        unread: groups.reduce((sum, g) => sum + (byGroup[g.id] ?? 0), 0),
      });
    }
    orgs.sort((a, b) => a.orgName.localeCompare(b.orgName));
    return orgs;
  }, [organizations, byGroup, search]);

  const searching = search.trim().length > 0;
  const hasAnyGroups = organizations.some((o) => (o.groups?.length ?? 0) > 0);

  return (
    <div className="flex h-full w-80 shrink-0 flex-col border-r border-flock-gray-200 bg-white">
      <div className="border-b border-flock-gray-200 p-3">
        <div className="relative">
          <MagnifyingGlassIcon className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-flock-navy-300" />
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search groups"
            className="w-full rounded-lg border border-flock-gray-200 bg-flock-navy-50 py-2 pl-9 pr-3 text-sm text-flock-navy-900 outline-none placeholder:text-flock-navy-300 focus:border-flock-navy-400"
          />
        </div>
      </div>

      <div className="flex-1 overflow-y-auto p-2">
        {/* Pinned combined views */}
        <button
          onClick={onSelectAll}
          className={clsx(
            'flex w-full items-center gap-3 rounded-lg px-3 py-2.5 text-left transition',
            selectedKey === 'all' ? 'bg-flock-navy-100' : 'hover:bg-flock-navy-50'
          )}
        >
          <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-flock-navy-600 text-white">
            <HandRaisedIcon className="h-5 w-5" />
          </div>
          <span className="min-w-0 flex-1 truncate text-sm font-semibold text-flock-navy-900">
            All Prayers
          </span>
          {totalUnread > 0 && (
            <span className="flex h-5 min-w-5 shrink-0 items-center justify-center rounded-full bg-flock-navy-600 px-1.5 text-xs font-semibold text-white">
              {totalUnread > 99 ? '99+' : totalUnread}
            </span>
          )}
        </button>

        <button
          onClick={onSelectLifted}
          className={clsx(
            'flex w-full items-center gap-3 rounded-lg px-3 py-2.5 text-left transition',
            selectedKey === 'lifted' ? 'bg-amber-100' : 'hover:bg-amber-50'
          )}
        >
          <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-gradient-to-br from-amber-400 to-amber-600 text-base">
            🕯️
          </div>
          <span className="min-w-0 flex-1 truncate text-sm font-semibold text-amber-900">
            Lifted
          </span>
        </button>

        <div className="my-2 h-px bg-flock-gray-100" />

        {!hasAnyGroups && (
          <div className="p-4 text-center text-sm text-flock-navy-400">
            You're not in any groups yet.
          </div>
        )}

        {hasAnyGroups && searching && tree.length === 0 && (
          <div className="p-4 text-center text-sm text-flock-navy-400">
            No groups match your search.
          </div>
        )}

        {tree.map((org) => {
          const orgKey = `org:${org.orgId}`;
          // While searching, force everything open so matches are visible
          const orgCollapsed = !searching && collapsed.has(orgKey);
          return (
            <div key={org.orgId} className="mb-1">
              <button
                onClick={() => toggleCollapsed(orgKey)}
                className="flex w-full items-center gap-1.5 rounded-lg px-2 py-2 text-left hover:bg-flock-navy-50"
              >
                {orgCollapsed ? (
                  <ChevronRightIcon className="h-3.5 w-3.5 shrink-0 text-flock-navy-400" />
                ) : (
                  <ChevronDownIcon className="h-3.5 w-3.5 shrink-0 text-flock-navy-400" />
                )}
                <BuildingLibraryIcon className="h-4 w-4 shrink-0 text-flock-navy-500" />
                <span className="min-w-0 flex-1 truncate text-xs font-bold uppercase tracking-wide text-flock-navy-700">
                  {org.orgName}
                </span>
                {isOrgLocked(org.orgId) && <PausedChip />}
                {orgCollapsed && org.unread > 0 && (
                  <span className="flex h-5 min-w-5 items-center justify-center rounded-full bg-flock-navy-600 px-1.5 text-[10px] font-semibold text-white">
                    {org.unread > 99 ? '99+' : org.unread}
                  </span>
                )}
              </button>

              {!orgCollapsed && (
                <div className="space-y-0.5">
                  {org.groups.map((group) => (
                    <PrayerGroupListItem
                      key={group.id}
                      group={group}
                      unread={byGroup[group.id] ?? 0}
                      selected={selectedKey === group.id}
                      onSelect={onSelectGroup}
                    />
                  ))}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
